import React, { useEffect, useState } from 'react';
import { organizationsService, type Organization, type OrganizationUpdateInput } from '../../services/organizationsService';

interface EditOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  organization: Organization | null;
}

const EditOrganizationModal: React.FC<EditOrganizationModalProps> = ({ isOpen, onClose, onSaved, organization }) => {
  const [formData, setFormData] = useState<OrganizationUpdateInput>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [allowedDomainsText, setAllowedDomainsText] = useState('');
  const [allowedIpsText, setAllowedIpsText] = useState('');

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name,
        subdomain: organization.subdomain,
        logo: organization.logo,
        active: organization.active,
        allowed_domains: organization.allowed_domains,
        allowed_ips: organization.allowed_ips,
        require_api_whitelist: organization.require_api_whitelist,
      });
      // Set text fields for JSON display
      setAllowedDomainsText(organization.allowed_domains ? JSON.stringify(organization.allowed_domains) : '');
      setAllowedIpsText(organization.allowed_ips ? JSON.stringify(organization.allowed_ips) : '');
    }
  }, [organization]);

  if (!isOpen || !organization) return null;

  const handleChange = (field: keyof OrganizationUpdateInput, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value as never }));
    if (errors[field as string]) setErrors(prev => ({ ...prev, [field as string]: '' }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.name) e.name = 'Organization name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      // Parse JSON text fields
      const allowedDomains = allowedDomainsText.trim() 
        ? JSON.parse(allowedDomainsText.trim())
        : [];
      
      const allowedIps = allowedIpsText.trim()
        ? JSON.parse(allowedIpsText.trim())
        : [];

      const dataToUpdate = {
        ...formData,
        allowed_domains: allowedDomains,
        allowed_ips: allowedIps
      };

      await organizationsService.update(organization.id, dataToUpdate);
      onSaved?.();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Organization</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 focus:outline-none">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
            <input 
              value={formData.name || ''} 
              onChange={e => handleChange('name', e.target.value)} 
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="Enter organization name"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain (optional)</label>
            <input 
              value={formData.subdomain || ''} 
              onChange={e => handleChange('subdomain', e.target.value)} 
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.subdomain ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="Enter subdomain (optional)"
            />
            {errors.subdomain && <p className="text-red-500 text-xs mt-1">{errors.subdomain}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
            <input 
              value={formData.logo || ''} 
              onChange={e => handleChange('logo', e.target.value)} 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter logo URL"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Active</label>
            <div className="flex items-center space-x-3">
              <button 
                type="button" 
                onClick={() => handleChange('active', !formData.active)} 
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.active ? 'bg-blue-600' : 'bg-gray-200'}`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${formData.active ? 'translate-x-5' : 'translate-x-0'}`}/>
              </button>
              <span className="text-sm text-gray-600">{formData.active ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Domains</label>
            <textarea 
              value={allowedDomainsText} 
              onChange={e => setAllowedDomainsText(e.target.value)} 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder='["example.com", "subdomain.example.com"]'
            />
            <p className="text-xs text-gray-500 mt-1">Enter as JSON array format. Example: ["example.com", "subdomain.example.com"]</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Allowed IPs</label>
            <textarea 
              value={allowedIpsText} 
              onChange={e => setAllowedIpsText(e.target.value)} 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder='["192.168.1.1", "10.0.0.1"]'
            />
            <p className="text-xs text-gray-500 mt-1">Enter as JSON array format. Example: ["192.168.1.1", "10.0.0.1"]</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Require API Whitelist</label>
            <div className="flex items-center space-x-3">
              <button 
                type="button" 
                onClick={() => handleChange('require_api_whitelist', !formData.require_api_whitelist)} 
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.require_api_whitelist ? 'bg-blue-600' : 'bg-gray-200'}`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${formData.require_api_whitelist ? 'translate-x-5' : 'translate-x-0'}`}/>
              </button>
              <span className="text-sm text-gray-600">{formData.require_api_whitelist ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">Cancel</button>
            <button type="submit" disabled={isSaving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors">{isSaving ? 'Saving...' : 'Update Data'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOrganizationModal;
